// Pure, framework-free upload-validation helpers shared by every UploadMedia
// entry point (file picker + dropzone drag-drop). Extracted so the type/size
// guard is a single source of truth and can be unit-tested directly.
//
// The caller passes the ALREADY-RESOLVED list of accepted extensions
// (`effectiveFormats` — which the component derives per-surface, e.g. the
// wallpaper picker strips "gif"), so this module owns no type map of its own.

export const MAX_FILE_SIZE_MB = 50;

// T22.677 (Gordon decision 2026-10-28, answers T22.676): video uploads have NO
// 50MB cap. The only per-file ceiling left for a video is the server's own
// presign guard (Backend PRESIGN_MAX_OBJECT_SIZE_BYTES, 256 MiB), which refuses
// to sign anything larger with a bare 400 — mirrored here so the user gets a
// plain message instead, and so a multi-GB file still never reaches S3 (C4.1).
// The account storage quota (402 storage_limit_exceeded) still applies on top.
export const VIDEO_MAX_FILE_BYTES = 256 * 1024 * 1024;
export const VIDEO_MAX_FILE_SIZE_MB = VIDEO_MAX_FILE_BYTES / 1000000;
export const VIDEO_MAX_FILE_SIZE_LABEL = "256MB";

/** Per-file size cap (MB, base-10 like isFileSizeAllowed) for an upload type. */
export const maxFileSizeMbForType = (type) =>
  type === "video" ? VIDEO_MAX_FILE_SIZE_MB : MAX_FILE_SIZE_MB;

/** Human label for that cap ("50MB" / "256MB"). */
export const maxFileSizeLabelForType = (type) =>
  type === "video" ? VIDEO_MAX_FILE_SIZE_LABEL : `${MAX_FILE_SIZE_MB}MB`;

export const getFileExtension = (name) =>
  String(name || "")
    .split(".")
    .pop()
    .toLowerCase();

export const isExtensionAllowed = (file, allowedExtensions) =>
  (allowedExtensions || []).includes(getFileExtension(file.name));

export const isFileSizeAllowed = (file, maxMB = MAX_FILE_SIZE_MB) =>
  file.size / 1000000 <= maxMB;

/**
 * Split an incoming file list into the files that pass BOTH the type and size
 * guard, plus a flag for whether anything was rejected on either axis.
 * @param {FileList|File[]} files
 * @param {string[]} allowedExtensions - resolved accepted extensions for the surface
 * @param {number} maxMB - size cap in MB
 * @returns {{ valid: File[], rejectedType: boolean, rejectedSize: boolean }}
 */
export const partitionUploadFiles = (
  files,
  allowedExtensions,
  maxMB = MAX_FILE_SIZE_MB
) => {
  const list = Array.from(files || []);
  const typeValid = list.filter((file) =>
    isExtensionAllowed(file, allowedExtensions)
  );
  const rejectedType = typeValid.length !== list.length;
  const valid = typeValid.filter((file) => isFileSizeAllowed(file, maxMB));
  const rejectedSize = valid.length !== typeValid.length;
  return { valid, rejectedType, rejectedSize };
};
